import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { hasTier } from "@/lib/tiers";
import { Search, Loader2, BookOpen, Scale, AlertTriangle, ShieldCheck, Compass, ArrowRight, Sparkles } from "lucide-react";
import { PaywallGate } from "@/components/PaywallGate";

const COPY = {
  en: {
    kicker: "DIY Legal · Complete +",
    heading: "Legal Research Assistant",
    sub: "Ask a plain-English question. Get back the rule, the source, and a plain-language explanation — never invented, always cited when a citation exists.",
    q_label: "What are you trying to figure out?",
    q_placeholder: "Example: What is the deadline to answer a summons in Illinois small-claims court?",
    j_label: "Jurisdiction (optional)",
    j_placeholder: "e.g., Illinois, Cook County, or Federal",
    submit: "Research this",
    loading: "Consulting authoritative sources...",
    reset: "Ask a new question",
    disclaimer: "DIY Legal is not a law firm. Results are educational and procedural only. Always verify with the applicable court, court rule, clerk, or a licensed attorney before acting.",
    labels: {
      rule: "Rule / Authority",
      source: "Source",
      plain: "Plain-language",
      why: "Why it may matter",
      you: "Your information",
      confidence: "Confidence",
      limits: "Limitations",
    },
    conf: { high: "High", medium: "Medium", low: "Low" },
    verify_prefix: "Verify",
    error_prefix: "Something went wrong:",
    empty: "No findings returned. Try rephrasing your question with a specific jurisdiction, deadline, or procedure.",
  },
  es: {
    kicker: "DIY Legal · Complete +",
    heading: "Asistente de Investigación Legal",
    sub: "Haz una pregunta en lenguaje sencillo. Recibe la regla, la fuente y una explicación sencilla — nunca inventada, siempre citada cuando existe una cita.",
    q_label: "¿Qué estás tratando de averiguar?",
    q_placeholder: "Ejemplo: ¿Cuál es el plazo para responder a una citación en la corte de small-claims de Illinois?",
    j_label: "Jurisdicción (opcional)",
    j_placeholder: "ej., Illinois, condado de Cook, o Federal",
    submit: "Investigar",
    loading: "Consultando fuentes autoritativas...",
    reset: "Hacer una nueva pregunta",
    disclaimer: "DIY Legal no es un bufete de abogados. Los resultados son educativos y procesales. Siempre verifica con la corte, la regla de la corte, la secretaría o un abogado con licencia antes de actuar.",
    labels: {
      rule: "Regla / Autoridad",
      source: "Fuente",
      plain: "Lenguaje sencillo",
      why: "Por qué puede importar",
      you: "Tu información",
      confidence: "Confianza",
      limits: "Limitaciones",
    },
    conf: { high: "Alta", medium: "Media", low: "Baja", alta: "Alta", media: "Media", baja: "Baja" },
    verify_prefix: "Verificar",
    error_prefix: "Algo salió mal:",
    empty: "No se devolvieron hallazgos. Intenta reformular tu pregunta con una jurisdicción, plazo o procedimiento específico.",
  },
};

function ConfidenceChip({ value, c }) {
  const key = (value || "").toLowerCase();
  const label = c.conf[key] || value || "—";
  const bg = key.startsWith("high") || key.startsWith("alt")
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : key.startsWith("low") || key.startsWith("baj")
      ? "bg-amber-100 text-amber-800 border-amber-300"
      : "bg-[#FAF5EC] text-[#1C2024] border-[#E4DDD3]";
  return <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border-2 ${bg}`}>{label}</span>;
}

function ResearchApp() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [question, setQuestion] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading) return;
    setLoading(true);
    setErr("");
    setResult(null);
    try {
      const { data } = await api.post("/research", {
        question: question.trim(),
        jurisdiction: jurisdiction.trim() || null,
        lang,
      });
      setResult(data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-6 px-1" data-testid="research-page">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32] font-extrabold inline-flex items-center gap-2">
          <Compass className="w-3.5 h-3.5" /> {c.kicker}
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-[#1C2024]">{c.heading}</h1>
        <p className="mt-2 text-[#4A525A] max-w-2xl mx-auto">{c.sub}</p>
      </div>

      <form onSubmit={submit} className="mt-8 bg-white border-2 border-[#E4DDD3] rounded-2xl p-5 sm:p-6 shadow-sm space-y-4" data-testid="research-form">
        <div>
          <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.q_label}</label>
          <textarea
            data-testid="research-question"
            className="mt-2 w-full min-h-[120px] rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-4 py-3 text-[#1C2024]"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={c.q_placeholder}
            maxLength={2000}
            required
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.j_label}</label>
          <input
            data-testid="research-jurisdiction"
            className="mt-2 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-4 py-2.5 text-[#1C2024]"
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            placeholder={c.j_placeholder}
            maxLength={120}
          />
        </div>
        <button
          type="submit"
          data-testid="research-submit"
          disabled={loading || !question.trim()}
          className="pod-focus w-full sm:w-auto bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-70 text-white font-bold py-3 px-6 rounded-xl inline-flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? c.loading : c.submit}
        </button>
      </form>

      {err && <div className="mt-4 text-sm text-red-800 bg-red-50 border-2 border-red-200 rounded-xl p-4" data-testid="research-error">{c.error_prefix} {err}</div>}

      {result && (
        <div className="mt-6 space-y-4" data-testid="research-results">
          {(!result.findings || result.findings.length === 0) && (
            <div className="rounded-2xl bg-[#FAF5EC] border-2 border-[#E4DDD3] p-5 text-[#4A525A]">{c.empty}</div>
          )}
          {result.findings?.map((f, i) => (
            <article key={i} data-testid={`research-finding-${i}`} className="rounded-2xl bg-white border-2 border-[#E4DDD3] p-5 sm:p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#C85A32]">
                  <Scale className="w-3.5 h-3.5" /> {c.labels.rule}
                </div>
                <ConfidenceChip value={f.confidence} c={c} />
              </div>
              <div className="mt-2 text-[#1C2024] font-bold text-lg leading-snug">{f.rule}</div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl bg-[#FAF5EC] border border-[#E4DDD3] p-3">
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#707880] flex items-center gap-1"><BookOpen className="w-3 h-3" /> {c.labels.source}</div>
                  <div className="mt-1 text-sm text-[#1C2024] font-mono break-words">{f.source}</div>
                </div>
                <div className="rounded-xl bg-[#FAF5EC] border border-[#E4DDD3] p-3">
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#707880]">{c.labels.you}</div>
                  <div className="mt-1 text-sm text-[#1C2024]">{f.user_information}</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#707880]">{c.labels.plain}</div>
                <p className="mt-1 text-[#1C2024] leading-relaxed">{f.plain_language}</p>
              </div>

              <div className="mt-4">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#707880]">{c.labels.why}</div>
                <p className="mt-1 text-[#1C2024] leading-relaxed">{f.why_it_matters}</p>
              </div>

              <div className="mt-4 rounded-xl bg-amber-50 border-2 border-amber-200 p-3">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-900 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {c.labels.limits}
                </div>
                <p className="mt-1 text-sm text-amber-900 leading-relaxed">{f.limitations}</p>
              </div>
            </article>
          ))}

          <div className="rounded-2xl bg-[#2C4A3E] text-white p-5" data-testid="research-verify">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-white/80">
              <ShieldCheck className="w-3.5 h-3.5" /> {c.verify_prefix}
            </div>
            <p className="mt-1 text-white/95 text-sm leading-relaxed">{result.verify_note}</p>
          </div>

          <button
            onClick={() => { setResult(null); setQuestion(""); setJurisdiction(""); }}
            data-testid="research-reset"
            className="pod-focus text-sm font-extrabold uppercase tracking-widest text-[#C85A32] hover:underline inline-flex items-center gap-1"
          >
            <Sparkles className="w-3.5 h-3.5" /> {c.reset} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <p className="mt-8 text-xs text-[#707880] italic text-center max-w-2xl mx-auto" data-testid="research-disclaimer">{c.disclaimer}</p>
    </div>
  );
}

export default function Research() {
  const { user } = useAuth();
  const tier = user?.tier || "free";
  if (!user || !hasTier(tier, "complete")) {
    return <PaywallGate feature="summarize" minimum="complete"><ResearchApp /></PaywallGate>;
  }
  return <ResearchApp />;
}
